import type { CommunicationRecord, VolunteerOrganization } from "@entities";
import { Chip, Stack, Typography } from "@mui/material";
import { type ColumnProps } from "@shared/frontend/hyper-table/types";
import {
  VOLUNTEER_ORG_EVENT_TYPES,
  isVolunteerOrgBranch,
  volunteerOrgEffectiveEventFlags,
  volunteerOrgHandlingManagerName,
  volunteerOrgKindLabel,
} from "@shared/helpers/volunteer-organization";
import { CommunicationRecordsTableCell } from "../../../components/CommunicationRecordManager/CommunicationRecordsTableCell";

export type VolunteerOrgTableView = "parents" | "branches";

export function buildVolunteerOrganizationColumns(
  view: VolunteerOrgTableView,
  onCommunicationSaved?: (
    organizationId: string,
    records?: CommunicationRecord[],
  ) => void,
): ColumnProps<VolunteerOrganization>[] {
  const nameColumn: ColumnProps<VolunteerOrganization> = {
    field: "name",
    caption: view === "parents" ? "ארגון" : "שלוחה",
    enableSorting: true,
    minSize: 240,
    cell: ({ row }) => {
      const org = row.original;
      return (
        <Stack sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>
            {org.name}
          </Typography>
          <Typography color="text.secondary" sx={{ fontSize: 12 }} noWrap>
            {volunteerOrgKindLabel(org)}
            {org.address ? ` · ${org.address}` : ""}
          </Typography>
        </Stack>
      );
    },
  };

  const commColumn: ColumnProps<VolunteerOrganization> = {
    field: "createdAt",
    caption: "תקשורת",
    minSize: 160,
    cell: ({ row }) => (
      <CommunicationRecordsTableCell
        records={row.original.communicationRecords}
        volunteerOrganizationId={row.original.id}
        title={row.original.name}
        onSave={(records) => onCommunicationSaved?.(row.original.id, records)}
      />
    ),
  };

  const managerColumn: ColumnProps<VolunteerOrganization> = {
    field: "handlingManagerId",
    caption: "מנהל מלווה",
    minSize: 180,
    cell: ({ row }) =>
      volunteerOrgHandlingManagerName(row.original, row.original.parent) || "",
  };

  if (view === "parents") {
    return [
      nameColumn,
      managerColumn,
      {
        field: "parentId",
        caption: "שלוחות",
        minSize: 220,
        cell: ({ row }) => {
          const org = row.original;
          if (isVolunteerOrgBranch(org)) {
            return org.address || "";
          }
          const branches = org.branches ?? [];
          if (branches.length === 0) {
            return (
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                אין שלוחות
              </Typography>
            );
          }
          return (
            <Stack sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                {branches.length} שלוחות
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 12 }} noWrap>
                {branches
                  .slice(0, 3)
                  .map((branch) => branch.name)
                  .join(" · ")}
                {branches.length > 3 ? ` +${branches.length - 3}` : ""}
              </Typography>
            </Stack>
          );
        },
      },
      commColumn,
    ];
  }

  return [
    nameColumn,
    {
      field: "parentId",
      caption: "ארגון אב",
      minSize: 180,
      cell: ({ row }) => row.original.parent?.name ?? "",
    },
    managerColumn,
    {
      field: "interestedInRoutine",
      caption: "סוג אירוע",
      minSize: 220,
      cell: ({ row }) => {
        const flags = volunteerOrgEffectiveEventFlags(
          row.original,
          row.original.parent,
        );
        return (
          <Stack direction="row" gap={0.5} flexWrap="wrap">
            {VOLUNTEER_ORG_EVENT_TYPES.filter((item) => flags[item.field]).map(
              (item) => (
                <Chip key={item.id} size="small" label={item.label} />
              ),
            )}
          </Stack>
        );
      },
    },
    {
      field: "volunteerCapacity",
      caption: "קיבולת",
      minSize: 90,
      cell: ({ row }) =>
        row.original.volunteerCapacity != null
          ? String(row.original.volunteerCapacity)
          : "",
    },
    commColumn,
  ];
}
